import { useUserSession } from '@solinkcorp/authentication';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import React, { type FC, useEffect, useMemo } from 'react';
import { useControllerState } from 'solox';
import CameraPlayer from 'src/core/video/components/players/CameraPlayer';
import { BLANK_IMAGE, useCameraThumbnail } from 'src/hooks/camera/useCameraThumbnail';
import { useDebouncedValue } from 'src/hooks/useDebounce';
import { useDeviceIsLocal } from 'src/hooks/useDeviceIsLocal';
import isShallowEqual from 'utils/isShallowEqual';
import { usePlaybackController } from '../../controller/usePlaybackController';
import { type ThumbnailLevel } from './VideoThumbnail';

export interface VideoThumbnailInnerProps {
  debounceDelay?: number;
  playHeadTime: number;
  playingCamera: PlayingCamera;
  setVideoRef?: (el: HTMLVideoElement | null) => void;
  thumbnailLevel?: ThumbnailLevel;
  segmentDurationMs?: number;
  timeZoneId: string;
}
const VideoThumbnailInner: FC<VideoThumbnailInnerProps> = ({
  debounceDelay = 0,
  playHeadTime,
  playingCamera,
  setVideoRef,
  thumbnailLevel,
  timeZoneId,
}) => {
  const debouncedPlayHeadTime = useDebouncedValue(playHeadTime, debounceDelay);
  const user = useUserSession();

  const deviceIsLocal = useDeviceIsLocal(playingCamera.device?.id);

  // Memoize the playingCameras array to prevent unnecessary controller recreation
  const playingCameras = useMemo(() => [playingCamera], [playingCamera]);
  const poster = useCameraThumbnail(playingCamera.camera);

  const controller = usePlaybackController(
    playingCameras,
    'sd',
    playHeadTime,
    {
      thumbnailLevel,
      quick: true,
      disableLiveModeBarrier: true,
    },
    undefined,
    undefined,
    timeZoneId,
  );
  useEffect(() => controller.togglePaused(true), [controller]);

  const bufferedSegments = useControllerState(
    controller.state,
    (state) => {
      return playingCamera && state.byPlayerId[playingCamera.playerId]?.status?.buffered;
    },
    isShallowEqual,
  );

  const isVideoAvailable = useMemo(
    () => bufferedSegments?.some(({ start, end }) => playHeadTime >= start && playHeadTime <= end),
    [bufferedSegments, playHeadTime],
  );
  useEffect(
    () => controller.seekToTime(isVideoAvailable ? playHeadTime : debouncedPlayHeadTime),
    [isVideoAvailable, playHeadTime, debouncedPlayHeadTime, controller],
  );

  const getObjectFit = (): 'cover' | 'fill' | 'contain' => {
    // TODO: find a better algo to determine the object fit of cameras with varying aspect ratios.
    return 'fill';
  };
  const objectFit = getObjectFit();

  return (
    <CameraPlayer
      // Force HLS, because we do not want to load the other players as we mouse over the live border.
      poster={poster || BLANK_IMAGE}
      videoPlayerType="HLS"
      style={{ opacity: isVideoAvailable ? '1' : '0', objectFit }}
      playbackController={controller}
      playingCamera={playingCamera}
      locationIsLocal={deviceIsLocal || false}
      user={user}
      // Thumbnail videos are always muted
      isMuted
      setVideoRef={setVideoRef}
    />
  );
};

export default React.memo(VideoThumbnailInner);
